import { Components, Formio } from "formiojs";
import FieldComponent from "formiojs/components/_classes/field/Field";

export class StopwatchComponent extends FieldComponent {
  private interval: any;
  private elapsedTime: number = 0;
  private running: boolean = false;
  private startTime: number = 0;

  addEventListener: any;
  refs: any;
  loadRefs: any;

  static schema(...extend: any[]): any {
    return FieldComponent.schema(
      {
        type: "stopwatch",
        label: "Stopwatch",
        key: "stopwatch",
        input: true,
        persistent: true,
      },
      ...extend
    );
  }

  static get builderInfo() {
    return {
      title: "Stop Watch",
      group: "basic",
      icon: "clock",
      weight: 80,
      schema: StopwatchComponent.schema(),
    };
  }

  get defaultSchema() {
    return StopwatchComponent.schema();
  }

  

  render(children: any) {
    return super.render(`
      <div class="stopwatch-container">
        <input ref="input" type="hidden" />
  
        <!-- Stopwatch body -->
        <div class="stopwatch">
          <div class="crown"></div>
          <div class="button"></div>
  
          <!-- Display -->
          <div class="display" ref="display">00:00:00</div>
        </div>
  
        <!-- Controls -->
        <div class="btn-group">
          <button type="button" ref="startBtn" class="btn btn-success btn-sm">Start</button>
          <button type="button" ref="stopBtn" class="btn btn-danger btn-sm">Stop</button>
          <button type="button" ref="resetBtn" class="btn btn-secondary btn-sm">Reset</button>
        </div>
      </div>
    `);
  }
  
  

  attach(element: any) {
    const refs = super.attach(element);

    this.loadRefs(element, {
      display: "single",
      startBtn: "single",
      stopBtn: "single",
      resetBtn: "single",
      input: "single",
    });

    // ✅ Make sure refs.input is always an array (Formio expects this)
    if (this.refs.input && !Array.isArray(this.refs.input)) {
      this.refs.input = [this.refs.input];
    }

    const updateDisplay = () => {
      if (this.refs.display) {
        const formattedTime = this.formatTime(this.elapsedTime);
        this.refs.display.textContent = formattedTime;

        // Update hidden input
        if (this.refs.input?.[0]) {
          this.refs.input[0].value = this.elapsedTime.toString();
        }
      }
    };

    if (this.refs.startBtn) {
      this.addEventListener(this.refs.startBtn, "click", (event) => {
        event.preventDefault();
        this.startTimer(updateDisplay);
      });
    }

    if (this.refs.stopBtn) {
      this.addEventListener(this.refs.stopBtn, "click", (event) => {
        event.preventDefault();
        this.stopTimer();
      });
    }

    if (this.refs.resetBtn) {
      this.addEventListener(this.refs.resetBtn, "click", (event) => {
        event.preventDefault();
        this.resetTimer(updateDisplay);
      });
    }

    updateDisplay();
    return refs;
  }

  startTimer(updateDisplay: () => void) {
    if (!this.running) {
      this.running = true;
      this.startTime = Date.now() - this.elapsedTime;

      if (this.interval) {
        clearInterval(this.interval);
      }

      this.interval = setInterval(() => {
        if (this.running) {
          this.elapsedTime = Date.now() - this.startTime;
          updateDisplay();
          this.updateValue();
        }
      }, 1000);
    }
  }

  stopTimer() {
    if (this.running) {
      this.running = false;
      clearInterval(this.interval);
      this.updateValue();
    }
  }

  resetTimer(updateDisplay: () => void) {
    this.running = false;
    clearInterval(this.interval);
    this.elapsedTime = 0;
    this.startTime = 0;
    updateDisplay();
    this.updateValue();
  }

  formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${this.padNumber(hours)}:${this.padNumber(minutes)}:${this.padNumber(seconds)}`;
  }

  padNumber(num: number): string {
    return num.toString().padStart(2, "0");
  }

  updateValue(value?: any, flags?: any) {
    if (this.refs?.input?.[0]) {
      this.refs.input[0].value = this.elapsedTime.toString();
    }
    return super.updateValue(this.elapsedTime, flags);
  }

  getValue() {
    return this.elapsedTime;
  }

  setValue(value: any, flags?: any) {
    if (typeof value === "number") {
      this.elapsedTime = value;
    } else if (typeof value === "string" && !isNaN(Number(value))) {
      this.elapsedTime = Number(value);
    } else {
      this.elapsedTime = 0;
    }

    if (this.refs?.input?.[0]) {
      this.refs.input[0].value = this.elapsedTime.toString();
    }

    return super.setValue(this.elapsedTime, flags);
  }

  destroy() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    super.destroy();
  }
}

// ✅ Register the component
Formio.use({
  components: {
    stopwatch: StopwatchComponent,
  },
});
